import type {ActionEnvelope, ChatState, ResponsePart, Turn} from '@microsoft/agent-host-protocol'
// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {createPresentationLossReporter, type ResultPresentationLoss} from './result-presentation-health'
import {readHostInstanceEvidence} from './host-instance-evidence'

export const MAX_RELAY_PRESENTATION_RECEIPTS = 256
export const MAX_RELAY_PRESENTATION_SUBSCRIBERS = 16

export interface ResultPresentationBinding {
  readonly task_id: string
  readonly session_id: string
  readonly environment_id: string
  readonly chat_id: string
}

export interface ResultPresentationReceipt extends ResultPresentationBinding {
  readonly source_event_id: string
  readonly upstream_source_event_id?: string
  readonly turn_id: string
  readonly part_id?: string
  readonly milestone_kind: 'content' | 'terminal'
  readonly receipt_source:
    | 'action'
    | 'snapshot_active_content'
    | 'snapshot_tracked_terminal'
    | 'snapshot_latest_terminal'
    | 'cache_active_content'
    | 'cache_latest_terminal'
  readonly terminal_state?: 'complete' | 'cancelled' | 'error'
  readonly terminal_provenance: 'live' | 'reconstructed' | 'provisional' | 'unknown'
  readonly observedAtMs: number
  readonly monotonicMs: number
  readonly visibility: 'visible' | 'hidden' | 'unknown'
}

type ObservationError = 'flag_evaluation' | 'observer' | 'clock' | 'emission' | 'observation_capacity'
let reported: Set<ObservationError> | undefined
let sourceOrdinal = 0
const sourceOrdinals = new WeakMap<ResultPresentationReceipt, number>()

export function resultPresentationSourceWatermark(): number {
  return sourceOrdinal
}

export function reportResultPresentationError(reason: ObservationError): void {
  if (reported?.has(reason)) return
  ;(reported ??= new Set()).add(reason)
  try {
    reportError(new Error(`Result presentation telemetry ${reason} failed`), {
      critical: false,
      reactAppName: 'copilot-result-presentation',
    })
  } catch {
    // eslint-disable-next-line no-console
    console.error('Unable to report result presentation telemetry error')
  }
}

export function isResultPresentationEnabled(): boolean {
  try {
    return isFeatureEnabled('one_copilot_chat')
  } catch {
    reportResultPresentationError('flag_evaluation')
    return false
  }
}

export function observeResultPresentation<T>(operation: () => T): T | undefined {
  try {
    return operation()
  } catch {
    reportResultPresentationError('observer')
    return undefined
  }
}

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object'
}

function identifier(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 512
}

function reconstructionTurnId(value: unknown): value is string {
  if (typeof value !== 'string' || !value) return false
  let scalars = 0
  for (const character of value) {
    const scalar = character.codePointAt(0)
    if (++scalars > 256 || scalar === undefined || (scalar >= 0xd800 && scalar <= 0xdfff)) return false
  }
  return true
}

export function readPresentationHostInstance(meta: unknown): string | undefined {
  if (!isResultPresentationEnabled()) return
  return observeResultPresentation(() => {
    const evidence = record(meta) ? meta['copilot.hostInstance'] : undefined
    if (evidence === undefined) return
    const id = readHostInstanceEvidence(meta)
    if (!id) {
      reportResultPresentationError('observer')
      return
    }
    return id
  })
}

export function reconstructedTerminalProvenance(
  state: ChatState,
  turnId: string,
): ResultPresentationReceipt['terminal_provenance'] {
  const evidence = state._meta?.['copilot.reconstruction']
  if (
    !record(evidence) ||
    evidence.version !== 1 ||
    evidence.source !== 'reconstructed' ||
    !reconstructionTurnId(evidence.throughTurnId) ||
    (evidence.provisionalTurnId !== undefined && !reconstructionTurnId(evidence.provisionalTurnId))
  ) {
    return 'unknown'
  }
  const boundary = state.turns.findIndex(turn => turn.id === evidence.throughTurnId)
  const index = state.turns.findIndex(turn => turn.id === turnId)
  if (boundary < 0 || index < 0 || index > boundary) return 'unknown'
  if (evidence.provisionalTurnId !== undefined) {
    const provisional = state.turns.findIndex(turn => turn.id === evidence.provisionalTurnId)
    if (provisional < 0 || provisional > boundary) return 'unknown'
  }
  return turnId === evidence.provisionalTurnId ? 'provisional' : 'reconstructed'
}

export function isMeaningfulPresentationPart(part: ResponsePart): boolean {
  return part.kind === 'markdown' && Boolean(part.content.trim())
}

function genuinelyActiveTurn(state: ChatState): ChatState['activeTurn'] {
  const active = state.activeTurn
  return active && !state.turns.some(turn => turn.id === active.id) ? active : undefined
}

export interface RelayResultPresentationSource {
  readonly exhausted: boolean
  snapshot(state: ChatState): void
  action(envelope: ActionEnvelope, previous: ChatState | undefined): void
  subscribe(
    listener: (receipt: ResultPresentationReceipt) => void,
    eligibleSince?: number,
    watermark?: number,
    onCoverageLost?: (loss: ResultPresentationLoss) => void,
  ): () => void
  adopt(state: ChatState, eligibleSince: number, watermark?: number): ResultPresentationReceipt[]
  dispose(): void
}

export function createRelayResultPresentationSource(
  binding: Omit<ResultPresentationBinding, 'chat_id'>,
): RelayResultPresentationSource | undefined {
  if (!isResultPresentationEnabled()) return undefined
  return observeResultPresentation(() => {
    const receipts = new Map<string, ResultPresentationReceipt>()
    const activeTurns = new Set<string>()
    const listeners = new Map<
      (receipt: ResultPresentationReceipt) => void,
      ((loss: ResultPresentationLoss) => void) | undefined
    >()
    let disposed = false
    let exhausted = false
    let latestLoss: ResultPresentationLoss | undefined

    const time = () => ({
      observedAtMs: Date.now(),
      monotonicMs: performance.now(),
      visibility:
        typeof document === 'undefined'
          ? ('unknown' as const)
          : document.visibilityState === 'visible'
            ? ('visible' as const)
            : ('hidden' as const),
    })
    const lost = createPresentationLossReporter({
      now: time,
      id: () => crypto.randomUUID(),
      emit: loss => {
        latestLoss = loss
        for (const listener of listeners.values()) {
          if (listener) observeResultPresentation(() => listener(loss))
        }
      },
    })
    const exhaust = (key: string, force = false) => {
      exhausted = true
      reportResultPresentationError('observation_capacity')
      lost(key, force)
    }
    const receive = (
      receipt: Omit<
        ResultPresentationReceipt,
        keyof typeof binding | keyof ReturnType<typeof time> | 'source_event_id'
      >,
      received: ReturnType<typeof time>,
    ) => {
      if (!identifier(receipt.chat_id) || !identifier(receipt.turn_id)) {
        reportResultPresentationError('observer')
        return
      }
      const key = JSON.stringify([receipt.chat_id, receipt.turn_id, receipt.milestone_kind])
      if (receipts.has(key)) return
      if (exhausted || receipts.size >= MAX_RELAY_PRESENTATION_RECEIPTS) {
        exhaust(key)
        return
      }
      const fact = {...binding, ...receipt, ...received, source_event_id: crypto.randomUUID()}
      if (!Number.isSafeInteger(sourceOrdinal + 1)) {
        reportResultPresentationError('clock')
        return
      }
      sourceOrdinals.set(fact, ++sourceOrdinal)
      receipts.set(key, fact)
      for (const listener of listeners.keys()) observeResultPresentation(() => listener(fact))
    }
    const terminal = (
      state: ChatState,
      turn: Turn,
      source: 'snapshot_tracked_terminal' | 'snapshot_latest_terminal',
      received: ReturnType<typeof time>,
    ) => {
      if (turn.state !== 'complete' && turn.state !== 'cancelled' && turn.state !== 'error') return
      receive(
        {
          chat_id: state.resource,
          turn_id: turn.id,
          milestone_kind: 'terminal',
          receipt_source: source,
          terminal_state: turn.state,
          terminal_provenance: reconstructedTerminalProvenance(state, turn.id),
        },
        received,
      )
      activeTurns.delete(turn.id)
    }

    return {
      get exhausted() {
        return exhausted
      },
      adopt(state, eligibleSince, watermark) {
        if (disposed) return []
        if (exhausted) {
          exhaust('adoption', true)
          return []
        }
        return (
          observeResultPresentation(() => {
            const active = genuinelyActiveTurn(state)
            const turn = active ?? state.turns.at(-1)
            if (!turn) return []
            if (!identifier(state.resource) || !identifier(turn.id)) {
              reportResultPresentationError('observer')
              return []
            }
            const received = time()
            const part = active?.responseParts.find(isMeaningfulPresentationPart)
            if (active && part?.kind !== 'markdown') return []
            const kind = active ? 'content' : 'terminal'
            const upstream = receipts.get(JSON.stringify([state.resource, turn.id, kind]))
            if (
              upstream &&
              (watermark === undefined
                ? upstream.monotonicMs >= eligibleSince
                : (sourceOrdinals.get(upstream) ?? -1) > watermark)
            )
              return []
            return [
              {
                ...binding,
                ...received,
                chat_id: state.resource,
                turn_id: turn.id,
                milestone_kind: kind,
                receipt_source: active ? 'cache_active_content' : 'cache_latest_terminal',
                source_event_id: crypto.randomUUID(),
                ...(upstream ? {upstream_source_event_id: upstream.source_event_id} : {}),
                ...(part?.kind === 'markdown' ? {part_id: part.id} : {}),
                ...('state' in turn ? {terminal_state: turn.state} : {}),
                terminal_provenance: reconstructedTerminalProvenance(state, turn.id),
              } satisfies ResultPresentationReceipt,
            ]
          }) ?? []
        )
      },
      snapshot(state) {
        if (disposed) return
        observeResultPresentation(() => {
          if (exhausted) {
            const active = genuinelyActiveTurn(state)
            const key = JSON.stringify([
              state.resource,
              active?.id ?? state.turns.at(-1)?.id,
              active ? 'content' : 'terminal',
            ])
            if (!receipts.has(key)) lost(key)
            return
          }
          const received = time()
          for (const turn of state.turns) {
            if (activeTurns.has(turn.id)) terminal(state, turn, 'snapshot_tracked_terminal', received)
          }
          const active = genuinelyActiveTurn(state)
          if (active) {
            if (!activeTurns.has(active.id) && activeTurns.size >= MAX_RELAY_PRESENTATION_RECEIPTS) {
              exhaust(active.id)
              return
            }
            activeTurns.add(active.id)
            const part = active.responseParts.find(isMeaningfulPresentationPart)
            if (part?.kind === 'markdown') {
              receive(
                {
                  chat_id: state.resource,
                  turn_id: active.id,
                  part_id: part.id,
                  milestone_kind: 'content',
                  receipt_source: 'snapshot_active_content',
                  terminal_provenance: 'unknown',
                },
                received,
              )
            }
          } else {
            const latest = state.turns.at(-1)
            if (latest) terminal(state, latest, 'snapshot_latest_terminal', received)
          }
        })
      },
      action(envelope, previous) {
        if (disposed || envelope.rejectionReason) return
        observeResultPresentation(() => {
          const action = envelope.action
          if (action.type === 'chat/turnStarted') {
            if (exhausted) {
              lost(JSON.stringify([envelope.channel, action.turnId, 'turn_started']))
              return
            }
            if (!activeTurns.has(action.turnId) && activeTurns.size >= MAX_RELAY_PRESENTATION_RECEIPTS) {
              exhaust(action.turnId)
              return
            }
            activeTurns.add(action.turnId)
            return
          }
          if (
            action.type !== 'chat/responsePart' &&
            action.type !== 'chat/delta' &&
            action.type !== 'chat/turnComplete' &&
            action.type !== 'chat/turnCancelled' &&
            action.type !== 'chat/error'
          ) {
            return
          }
          const received = time()
          if (previous?.turns.some(turn => turn.id === action.turnId)) return
          const base = {chat_id: envelope.channel, turn_id: action.turnId, receipt_source: 'action' as const}
          if (
            action.type === 'chat/turnComplete' ||
            action.type === 'chat/turnCancelled' ||
            action.type === 'chat/error'
          ) {
            receive(
              {
                ...base,
                milestone_kind: 'terminal',
                terminal_state:
                  action.type === 'chat/turnComplete'
                    ? 'complete'
                    : action.type === 'chat/turnCancelled'
                      ? 'cancelled'
                      : 'error',
                terminal_provenance: 'live',
              },
              received,
            )
            activeTurns.delete(action.turnId)
            return
          }
          const part =
            action.type === 'chat/responsePart'
              ? action.part
              : previous?.activeTurn?.id === action.turnId
                ? previous.activeTurn.responseParts.find(
                    candidate => 'id' in candidate && candidate.id === action.partId,
                  )
                : undefined
          if (
            part?.kind === 'markdown' &&
            (action.type === 'chat/delta' ? Boolean(action.content.trim()) : isMeaningfulPresentationPart(part))
          ) {
            receive({...base, part_id: part.id, milestone_kind: 'content', terminal_provenance: 'unknown'}, received)
          }
        })
      },
      subscribe(listener, eligibleSince = 0, watermark, onCoverageLost) {
        if (disposed) return () => undefined
        if (exhausted || listeners.size >= MAX_RELAY_PRESENTATION_SUBSCRIBERS) {
          if (listeners.size < MAX_RELAY_PRESENTATION_SUBSCRIBERS) listeners.set(listener, onCoverageLost)
          exhaust('subscription', true)
          const loss = latestLoss
          if (!listeners.has(listener) && loss && onCoverageLost) observeResultPresentation(() => onCoverageLost(loss))
          return () => listeners.delete(listener)
        }
        listeners.set(listener, onCoverageLost)
        // A selected view may learn its session identity after the live receipt.
        // Cached receipts from before that view's intent belong to recovery.
        for (const receipt of receipts.values()) {
          if (
            watermark === undefined
              ? receipt.monotonicMs >= eligibleSince
              : (sourceOrdinals.get(receipt) ?? -1) > watermark
          ) {
            observeResultPresentation(() => listener(receipt))
          }
        }
        return () => listeners.delete(listener)
      },
      dispose() {
        disposed = true
        listeners.clear()
        receipts.clear()
        activeTurns.clear()
      },
    }
  })
}
